// components/TaskItem.tsx
import React, { useEffect, useRef } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  InputAdornment,
  IconButton,
  Link,
} from '@mui/material';
import { PlayArrow, Stop, Delete, OpenInNew } from '@mui/icons-material';
import { Task } from '../types';

interface TaskItemProps {
  task: Task;
  onUpdateTime: (taskId: string, timeSpent: number) => void;
  onUpdateComment: (taskId: string, comment: string) => void;
  onToggleTimer: (taskId: string) => void;
  onDeleteTask: (taskId: string) => void;
}

export const TaskItem: React.FC<TaskItemProps> = ({
  task,
  onUpdateTime,
  onUpdateComment,
  onToggleTimer,
  onDeleteTask,
}) => {
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (task.isRunning) {
      intervalRef.current = setInterval(() => {
        onUpdateTime(task.id, task.timeSpent + 1);
      }, 1000);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [task.isRunning, task.timeSpent, task.id, onUpdateTime]);

  const formatTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes
      .toString()
      .padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleTimeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Конвертируем минуты в секунды для внутреннего хранения (целые минуты)
    const timeInMinutes = Math.max(0, parseInt(event.target.value) || 0);
    const timeInSeconds = timeInMinutes * 60;
    onUpdateTime(task.id, timeInSeconds);
  };

  const handleCommentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onUpdateComment(task.id, event.target.value);
  };

  // Конвертируем секунды в целые минуты для отображения в инпуте
  const timeInMinutes = Math.round(task.timeSpent / 60);

  return (
    <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
      <Box display="flex" alignItems="flex-start" gap={2}>
        <Box flex={1}>
          <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
            <Link
              href={task.webPath}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                textDecoration: 'none',
                color: 'primary.main',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              <Typography variant="body1" fontWeight="medium" component="span">
                {task.name}
              </Typography>
            </Link>
            <IconButton
              size="small"
              href={task.webPath}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ p: 0.5 }}
            >
              <OpenInNew fontSize="small" />
            </IconButton>
          </Box>

          <Typography variant="subtitle1" color="text.primary" gutterBottom>
            Проект: {task.projectName}
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {task.slug}
          </Typography>

          <TextField
            fullWidth
            size="small"
            placeholder="Добавить комментарий..."
            value={task.comment}
            onChange={handleCommentChange}
            variant="outlined"
            multiline
            maxRows={3}
          />
        </Box>

        <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
          <TextField
            type="number"
            value={timeInMinutes}
            onChange={handleTimeChange}
            size="small"
            sx={{ width: 130 }} // увеличена ширина до 130
            InputProps={{
              endAdornment: <InputAdornment position="end">мин</InputAdornment>,
              inputProps: { min: 0 },
            }}
            disabled={task.isRunning}
          />

          <Typography variant="body2" sx={{ minWidth: 80, textAlign: 'center' }}>
            {formatTime(task.timeSpent)}
          </Typography>
        </Box>

        <Box display="flex" flexDirection="column" gap={1}>
          <Button
            variant={task.isRunning ? 'contained' : 'outlined'}
            color={task.isRunning ? 'secondary' : 'primary'}
            onClick={() => onToggleTimer(task.id)}
            startIcon={task.isRunning ? <Stop /> : <PlayArrow />}
            size="small"
          >
            {task.isRunning ? 'Stop' : 'Start'}
          </Button>

          <IconButton
            color="error"
            onClick={() => onDeleteTask(task.id)}
            size="small"
            title="Удалить задачу"
          >
            <Delete />
          </IconButton>
        </Box>
      </Box>
    </Paper>
  );
};
